import React, { useCallback, useMemo } from "react";
import {
  buildPathWithParams,
  UrlParams,
  useMediaQuery,
  useQueryString,
} from "../hooks";
import { useI18n } from "../lib/i18n/i18n_provider";
import { uniqueBy } from "@/lib/js_utils/array_utils";
import { useHistory } from "react-router-dom";
import { LocationFilter } from "@/offering_v2/components/filters/location_filter";
import { useLocationPickerState } from "@/offering_v2/hooks/use_location_picker_state";
import { FilterLocation, Partner } from "@/data/partner_offering";
import "./catalog_location_filter.css";
import classNames from "classnames";
import { useLocationParam } from "@/offering_v2/hooks/use_location_param";
import { IconName } from "@/components";
import { Option } from "react-select/src/filters";

interface Filter {
  label: string;
  iconName: IconName;
  options: Option[];
  value?: string;
  onChange: (value?: string) => void;
}
interface CatalogLocationFilterProps {
  partner: Partner;
  locations: FilterLocation[];
}

export interface CatalogLocationFilter extends UrlParams {
  city?: string;
  cities?: string[];
  locationID?: string;
  locationIDs?: string[];
  state?: string;
}

export function CatalogLocationFilter({
  partner,
  locations,
}: CatalogLocationFilterProps) {
  const { locationID, city, state } = useQueryString();
  const { cities, locationIDs } = useLocationParam(locations, false);
  const mq = useMediaQuery();
  const history = useHistory();

  const filterValue = useMemo(() => {
    return {
      locationID,
      city,
      cities,
      state,
      locationIDs,
    };
  }, [locationID, city, cities, state, locationIDs]);

  const { fbt } = useI18n();

  const {
    displayedValue,
    selectedCities,
    selectedLocationIDs,
    handleSelectCity,
    handleSelectLocation,
    handleClearSelection: handleClearLocationSelection,
  } = useLocationPickerState(
    locations,
    {
      cities,
      locationIDs,
    },
    false
  );

  const options = useMemo(() => {
    return (
      locations?.map((l: FilterLocation) => ({
        label: l.locationDisplay,
        value: l.id,
        data: { city: l.city, state: l.state },
      })) || []
    );
  }, [locations]);

  const handleChangeFilter = useCallback(
    (filter: CatalogLocationFilter) => {
      const pathWithParams = buildPathWithParams(
        window.location.pathname,
        filter
      );
      history.replace(pathWithParams);
    },
    [history]
  );

  const uniqueOptions = useMemo(() => {
    return uniqueBy(options, (o) => o.value);
  }, [options]);
  const locationFilter: Filter = useMemo(() => {
    return {
      label: fbt("Location"),
      iconName: "location",
      options: uniqueOptions,
      value: uniqueOptions.some((opt) => opt.value === filterValue.locationID)
        ? filterValue.locationID
        : "",
      onChange: (locationID: string | undefined) =>
        handleChangeFilter({ ...filterValue, locationID }),
    };
  }, [fbt, filterValue, handleChangeFilter, uniqueOptions]);

  const handleClearFilters = () => {
    handleClearLocationSelection();
    handleChangeFilter({
      ...filterValue,
      locationID: undefined,
      locationIDs: undefined,
      cities: undefined,
      city: undefined,
    });
  };

  return (
    <div className="container-gallery-filter">
      <div
        className={classNames("filtersWrapper container", {
          "filtersWrapper--mobile": mq.device === "mobile",
        })}
      >
        <LocationFilter
          partner={partner}
          filters={{ location: locationFilter }}
          locations={locations}
          displayedValue={displayedValue}
          values={{
            cities: selectedCities,
            locationIDs: selectedLocationIDs,
          }}
          onClearSelection={handleClearLocationSelection}
          onSelectCity={handleSelectCity}
          onSelectLocation={handleSelectLocation}
          isMultiSelect={false}
        />
        {locationIDs && mq.device !== "mobile" && (
          <button
            className="btn btn-link fw-normal ms-auto"
            style={{ color: "var(--bs-secondary)" }}
            onClick={handleClearFilters}
          >
            {fbt("Clear filters")}
          </button>
        )}
      </div>
    </div>
  );
}
